import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import { Button } from '@/src/renderer/components/ui/Button'
import { SegmentedControl } from '@/src/renderer/components/ui/SegmentedControl'
import type { RecurrenceRule, ReminderAnchor } from '@/src/renderer/lib/db/models'
import { dateKey, parseDateKey, weekday } from '@/src/renderer/lib/date/calendar'
import { WORKDAYS } from '@/src/renderer/lib/recurrence/presets'
import { beforeAnchor } from '@/src/renderer/lib/reminders/schedule'
import { relativeReminderLabel, reminderWhenLabel, silenceLabel } from '../../utils/reminderLabels'
import type { ReminderDropdownContentProps, ReminderDropdownTab } from './ReminderDropdown.types'
import './ReminderDropdown.scss'

/** Die Vorschläge unter „Vorher“, wie im Prototyp: zur Uhrzeit bis einen Tag vorher. */
const OFFSETS = [0, 5, 15, 30, 60, 120, 1440] as const
const TIMES = ['09:00', '12:00', '15:00', '18:00', '20:00'] as const

type Rhythm = 'once' | 'daily' | 'weekdays' | 'weekly'

function ruleOf(rhythm: Rhythm, date: string): RecurrenceRule | null {
  switch (rhythm) {
    case 'once': return null
    case 'daily': return { type: 'fixed', interval: 1, unit: 'day' }
    case 'weekdays': return { type: 'fixed', interval: 1, unit: 'week', weekdays: [...WORKDAYS] }
    case 'weekly': return { type: 'fixed', interval: 1, unit: 'week', weekdays: [weekday(parseDateKey(date))] }
  }
}

const at = (date: string, time: string) => {
  const day = parseDateKey(date)
  const [h, m] = time.split(':').map(Number)
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), h ?? 0, m ?? 0)
}

const clock = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)

/**
 * **Eine Erinnerung anlegen** (Plan task-reminders, Etappe 5) — zwei Reiter wie im Prototyp:
 *
 * - **Vorher**: Bezug Wann oder Deadline, Vorschläge von „Zur Uhrzeit“ bis „1 Tag“, jeder mit der
 *   Uhrzeit, zu der er klingelt; darunter eigene Minuten. Hat der Bezug keine Uhrzeit, sind die
 *   Vorschläge gesperrt und der Hinweis sagt, warum — und dass „Datum & Uhrzeit“ geht.
 * - **Datum & Uhrzeit**: ein fester Zeitpunkt, wahlweise mit eigenem Rhythmus (Einmal, Täglich,
 *   Werktags, Wöchentlich). Eine Uhrzeit antippen legt an.
 *
 * Ein Klick legt sofort an (ADR 0026) — kein Bestätigen, kein Schließen: Wer zwei will, klickt zwei.
 *
 * @example
 * <ReminderDropdownContent anchors={rows.anchors} anchorSilence={rows.anchorSilence}
 *   existing={[{ anchor: 'start', offsetMinutes: 30 }]} onAdd={draft => createReminder(taskId, draft)} />
 */
export const ReminderDropdownContent: React.FC<ReminderDropdownContentProps> = ({
  anchors, anchorSilence, existing = [], onAdd, placement = 'badge', defaultTab = 'before', now: nowProp,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de'
  const now = nowProp ?? new Date()
  const [tab, setTab] = useState<ReminderDropdownTab>(defaultTab)
  // Der Bezug, der klingeln kann, zuerst — sonst stünde man vor lauter gesperrten Vorschlägen.
  const [anchor, setAnchor] = useState<ReminderAnchor>(
    anchorSilence.start && !anchorSilence.deadline ? 'deadline' : 'start',
  )
  const [custom, setCustom] = useState('')
  const [date, setDate] = useState(() => dateKey(now))
  const [rhythm, setRhythm] = useState<Rhythm>('once')
  const [time, setTime] = useState('')

  const anchorAt = anchors[anchor]
  const silence = anchorSilence[anchor]
  const has = (offset: number) => existing.some(e => e.anchor === anchor && e.offsetMinutes === offset)
  const addBefore = (offsetMinutes: number) => onAdd({ kind: 'relative', anchor, offsetMinutes })
  const customMinutes = Number.parseInt(custom, 10)
  const customValid = Number.isFinite(customMinutes) && customMinutes > 0 && !has(customMinutes)

  // Einmalig heute und schon vorbei: Das klänge nie.
  const past = (clockTime: string) => rhythm === 'once' && at(date, clockTime) <= now
  const addAt = (clockTime: string) => {
    onAdd({ kind: 'absolute', remindDate: date, remindTime: clockTime, recurrence: ruleOf(rhythm, date) })
  }

  return (
    <div className="reminder-dropdown" data-placement={placement}>
      <SegmentedControl<ReminderDropdownTab>
        className="reminder-dropdown__tabs"
        value={tab}
        onValueChange={setTab}
        options={[
          { value: 'before', label: t('reminders.dropdown.tabBefore') },
          { value: 'at', label: t('reminders.dropdown.tabAt') },
        ]}
      />

      {tab === 'before' ? (
        <div className="reminder-dropdown__panel">
          <SegmentedControl<ReminderAnchor>
            aria-label={t('reminders.dropdown.anchor')}
            value={anchor}
            onValueChange={setAnchor}
            options={[
              { value: 'start', label: t('taskDetails.section.start') },
              { value: 'deadline', label: t('taskDetails.section.deadline') },
            ]}
          />
          <p className="reminder-dropdown__hint" data-warn={silence ? '' : undefined}>
            {anchorAt && !silence
              ? t('reminders.dropdown.anchorAt', { when: reminderWhenLabel(anchorAt, now, t, locale) })
              : t('reminders.dropdown.anchorSilent', { reason: silenceLabel(silence ?? 'noWann', t) })}
          </p>
          <div className="reminder-dropdown__options">
            {OFFSETS.map(offset => {
              const selected = has(offset)
              return (
                <button
                  key={offset}
                  type="button"
                  className={`context-menu-content__item reminder-dropdown__item${selected ? ' reminder-dropdown__item--selected' : ''}`}
                  disabled={!!silence || !anchorAt || selected}
                  onClick={() => addBefore(offset)}
                >
                  {relativeReminderLabel(anchor, offset, t)}
                  <span className="reminder-dropdown__item-right">
                    {selected
                      ? <CheckCircleFilledIcon size={16} className="reminder-dropdown__check" />
                      : anchorAt && !silence && clock(beforeAnchor(anchorAt, offset), locale)}
                  </span>
                </button>
              )
            })}
          </div>
          <div className="reminder-dropdown__row">
            <input
              className="reminder-dropdown__field reminder-dropdown__field--minutes"
              type="number"
              min={1}
              inputMode="numeric"
              aria-label={t('reminders.dropdown.customMinutes')}
              placeholder={t('reminders.dropdown.customMinutes')}
              value={custom}
              disabled={!!silence || !anchorAt}
              onChange={e => setCustom(e.target.value)}
              onKeyDown={e => {
                e.stopPropagation()
                if (e.key === 'Enter' && customValid) { addBefore(customMinutes); setCustom('') }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!!silence || !anchorAt || !customValid}
              onClick={() => { addBefore(customMinutes); setCustom('') }}
            >
              {t('reminders.dropdown.customAdd')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="reminder-dropdown__panel">
          <input
            className="reminder-dropdown__field"
            type="date"
            aria-label={t('reminders.dropdown.date')}
            value={date}
            onChange={e => { if (e.target.value) setDate(e.target.value) }}
            onKeyDown={e => e.stopPropagation()}
          />
          <SegmentedControl<Rhythm>
            className="reminder-dropdown__rhythm"
            aria-label={t('reminders.dropdown.rhythm')}
            value={rhythm}
            onValueChange={setRhythm}
            options={[
              { value: 'once', label: t('reminders.dropdown.once') },
              { value: 'daily', label: t('reminders.dropdown.daily') },
              { value: 'weekdays', label: t('reminders.dropdown.weekdays') },
              { value: 'weekly', label: t('reminders.dropdown.weekly') },
            ]}
          />
          <p className="reminder-dropdown__hint">{t('reminders.dropdown.atHint')}</p>
          <div className="reminder-dropdown__times">
            {TIMES.map(clockTime => (
              <button
                key={clockTime}
                type="button"
                className="reminder-dropdown__time"
                disabled={past(clockTime)}
                onClick={() => addAt(clockTime)}
              >
                {clock(at(date, clockTime), locale)}
              </button>
            ))}
          </div>
          <div className="reminder-dropdown__row">
            <input
              className="reminder-dropdown__field"
              type="time"
              aria-label={t('reminders.dropdown.time')}
              value={time}
              onChange={e => setTime(e.target.value)}
              onKeyDown={e => {
                e.stopPropagation()
                if (e.key === 'Enter' && time && !past(time)) { addAt(time); setTime('') }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!time || past(time)}
              onClick={() => { addAt(time); setTime('') }}
            >
              {t('reminders.dropdown.atAdd')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
